import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Box from "react-raster";
import { H2, Copy } from "../Typography";
import Container from "../Container";
import BigImage from "../BigImage";
import { Media } from "../Media";
import Cards from "../Cards";
import CareersList from "../CareersList";
import ContentArea from "../ContentArea";
import prefersReducedMotion from "../../prefersReducedMotion";

const Title = styled.div`
  margin-bottom: ${({ $hasBottomMargin }) => ($hasBottomMargin ? "80px" : 0)};
`;

const Marquee = styled.div`
  overflow: hidden;
  max-width: 100vw;
`;

const innerStyles = `
  display: flex;
  width: 100%;
  max-width: 80%;

  @media (${({ theme }) => theme.mq.l}) { 
    max-width: 100%;
  }
`;

const Inner = prefersReducedMotion()
  ? styled.div`
      ${innerStyles}
    `
  : styled(motion.div)`
      ${innerStyles}
    `;

const Item = styled.span`
  padding: 0 0.5em 0 0;
  white-space: nowrap;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colours.lightGrey};
  margin: 90px auto;
  max-width: calc(100vw - 60px);
  height: 0;
  background: none;
`;

const Content = styled.div`
  background-color: ${({ theme, backgroundColour = "default" }) =>
    theme.colours[backgroundColour]};
`;

const spring = {
  type: "spring",
  damping: 10,
  stiffness: 50,
  delay: "2.5s",
};

function MarqueeTitleSection({
  title,
  hasTopBorder,
  $hasBottomMargin,
  backgroundColour,
  description,
  subdescription,
  content,
  anchorId,
}) {
  const items = [title, title, title, title, title, title]; // fill that viewport
  const { ref, inView } = useInView({
    threshold: 1,
  });
  const colour =
    !backgroundColour || backgroundColour === "default" ? "black" : "white";

  return (
    <>
      <Title $hasBottomMargin={$hasBottomMargin} ref={ref} id={anchorId}>
        {hasTopBorder && <Divider />}
        <Media lessThan="l">
          <Marquee>
            <Inner
              initial={{ x: "100vw" }}
              transition={spring}
              animate={{ x: inView && 0 }}
            >
              <H2 style={{ lineHeight: "66px" }}>
                <em>{title}</em>
              </H2>
            </Inner>
          </Marquee>
        </Media>
        <Media greaterThanOrEqual="l">
          <Marquee>
            <Inner
              initial={{ x: "100vw" }}
              transition={spring}
              animate={{ x: inView && 0 }}
            >
              {items.map((item, i) => (
                <Item key={i.toString()}>
                  <H2 style={{ lineHeight: "86px" }}>
                    <em>{item}</em>
                  </H2>
                </Item>
              ))}
            </Inner>
          </Marquee>
        </Media>
      </Title>
      {(description || subdescription || content.image) && (
        <Content backgroundColour={backgroundColour}>
          <Container as="div">
            {description && (
              <Box
                cols={[6, 6, 5]}
                marginLeft={[0, 0, 1]}
                marginTop={backgroundColour && 1}
              >
                <Copy colour={colour} medium>
                  {description}
                </Copy>
              </Box>
            )}
            {subdescription && (
              <Box
                cols={[6, 6, 5]}
                marginRight={[0, 0, 1]}
                marginTop={backgroundColour && 1}
                marginBottom={1}
              >
                <Copy colour={colour}>{subdescription}</Copy>
              </Box>
            )}
            {content.__typename === "ContentfulBigImage" && (
              <Box cols={[6, 12, 10]} marginLeft={[0, 0, 1]}>
                <BigImage {...content} />
              </Box>
            )}
            {content.__typename === "ContentfulCareersList" && (
              <Box cols={[6, 6, 10]} marginLeft={[0, 0, 1]} marginBottom={1}>
                <CareersList colour={colour} />
              </Box>
            )}
          </Container>
        </Content>
      )}
      {/**
       * We pass the current backgroundColour after spreading in the content component;
       * therefore the current background colour takes precedence over the content component's
       * background colour.
       *  */}
      {content.__typename === "ContentfulContentArea" && (
        <ContentArea {...content} backgroundColour={backgroundColour} />
      )}
      {content.__typename === "ContentfulCards" && (
        <Cards {...content} backgroundColour={backgroundColour} />
      )}
    </>
  );
}

export default MarqueeTitleSection;
